/**
 * /periods/:periodId/checklist — Period close pre-flight checklist.
 *
 * Standalone page that shows the full pre-flight report for a period
 * before closing it. Fetches GET /api/periods/{periodId}/close-checklist,
 * then lets the accountant close via POST /api/periods/{periodId}/close.
 *
 * The blockers / warnings in the API response are plain strings. The
 * existing CloseChecklist type in types.ts wraps them as {code,message}
 * objects — the API actually returns { blockers: string[], warnings: string[] }.
 * This page handles the wire format directly rather than relying on types.ts.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";

import { api } from "@/lib/api";
import { periodKeys } from "@/lib/queries";

export const Route = createFileRoute("/_auth/periods/$periodId/checklist")({
  component: PeriodChecklistPage,
});

// ── Wire-format shapes returned by the backend ──────────────────────────────
interface ChecklistResponse {
  period_id?: string;
  period_no?: number;
  start_date?: string;
  end_date?: string;
  // blockers + warnings come back as plain strings from the service
  blockers: string[];
  warnings: string[];
  stats: {
    period_no?: number;
    start_date?: string;
    end_date?: string;
    draft_journal_count?: number;
    posted_journal_count?: number;
    [k: string]: unknown;
  };
}

// ── Icon + colour helpers ────────────────────────────────────────────────────
function BlockerRow({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 border border-red-200 bg-red-50/60 rounded px-3 py-2">
      <ErrorIcon className="text-red-600 mt-0.5 shrink-0" fontSize="small" />
      <span className="text-sm text-red-800">{message}</span>
    </div>
  );
}

function WarningRow({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 border border-amber-200 bg-amber-50/60 rounded px-3 py-2">
      <WarningAmberIcon className="text-amber-600 mt-0.5 shrink-0" fontSize="small" />
      <span className="text-sm text-amber-800">{message}</span>
    </div>
  );
}

function PassRow({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 border border-emerald-200 bg-emerald-50/40 rounded px-3 py-2">
      <CheckCircleIcon className="text-emerald-600 mt-0.5 shrink-0" fontSize="small" />
      <span className="text-sm text-emerald-800">{message}</span>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number | undefined }) {
  return (
    <div className="border border-slate-200 rounded p-3 bg-slate-50 dark:bg-gray-950 dark:border-gray-700 flex flex-col gap-0.5">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className="text-sm font-mono font-semibold text-slate-800 dark:text-slate-200">
        {value ?? "—"}
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
function PeriodChecklistPage() {
  const { periodId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [forceWarnings, setForceWarnings] = useState(false);
  const [notes, setNotes] = useState("");
  const [closeError, setCloseError] = useState<string | null>(null);
  const [closeSuccess, setCloseSuccess] = useState(false);

  // ── Fetch checklist ─────────────────────────────────────────────────────
  const checklistQ = useQuery<ChecklistResponse>({
    queryKey: ["period-checklist", periodId],
    queryFn: () => api<ChecklistResponse>(`/api/periods/${periodId}/close-checklist`),
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });

  // ── Close mutation ──────────────────────────────────────────────────────
  const closeMut = useMutation({
    mutationFn: async () => {
      const params = new URLSearchParams();
      if (forceWarnings) params.set("force_warnings", "true");
      if (notes.trim()) params.set("notes", notes.trim());
      const qs = params.toString();
      return api(`/api/periods/${periodId}/close${qs ? `?${qs}` : ""}`, { method: "POST" });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: periodKeys.all });
      qc.invalidateQueries({ queryKey: ["period-checklist", periodId] });
      setCloseSuccess(true);
      setCloseError(null);
    },
    onError: (err: Error) => {
      setCloseError(err.message);
      checklistQ.refetch();
    },
  });

  const checklist = checklistQ.data;
  const blockers: string[] = checklist?.blockers ?? [];
  const warnings: string[] = checklist?.warnings ?? [];
  const hasBlockers = blockers.length > 0;
  const hasWarnings = warnings.length > 0;
  const allClear = !hasBlockers && !hasWarnings;
  const canClose =
    !hasBlockers &&
    (!hasWarnings || forceWarnings) &&
    !closeMut.isPending &&
    !closeSuccess;

  // ── Derive a human-readable period label ───────────────────────────────
  const stats = checklist?.stats ?? {};
  const periodLabel = stats.period_no ? `P${stats.period_no}` : `Period`;
  const dateRange =
    stats.start_date && stats.end_date
      ? ` · ${stats.start_date} → ${stats.end_date}`
      : "";

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <Link to="/periods">
            <Button startIcon={<ArrowBackIcon />} size="small" sx={{ textTransform: "none" }}>
              Back to Periods
            </Button>
          </Link>
          <Divider orientation="vertical" flexItem />
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Close Checklist — {periodLabel}
            <span className="text-sm font-normal text-slate-500 ml-2 dark:text-slate-400">
              {dateRange}
            </span>
          </h1>
          <div className="flex-1" />
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => checklistQ.refetch()}
            disabled={checklistQ.isFetching}
            sx={{ textTransform: "none" }}
          >
            {checklistQ.isFetching ? "Re-checking…" : "Re-run checks"}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-5">
        {checklistQ.isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {checklistQ.isError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load checklist: {(checklistQ.error as Error).message}
          </Alert>
        )}

        {closeSuccess && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
            action={
              <Button
                size="small"
                color="inherit"
                onClick={() => navigate({ to: "/periods" })}
              >
                Back to Periods
              </Button>
            }
          >
            Period {periodLabel} closed successfully.
          </Alert>
        )}

        {closeError && !closeSuccess && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setCloseError(null)}>
            Close failed: {closeError}
          </Alert>
        )}

        {checklist && (
          <div className="flex flex-col gap-5 max-w-2xl">
            {/* Stats grid */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                Period Stats
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Period" value={stats.period_no ? `P${stats.period_no}` : undefined} />
                <StatCard label="Start" value={stats.start_date as string | undefined} />
                <StatCard label="End" value={stats.end_date as string | undefined} />
                <StatCard label="Posted journals" value={stats.posted_journal_count as number | undefined} />
                {typeof stats.draft_journal_count === "number" && (
                  <StatCard label="Draft journals" value={stats.draft_journal_count} />
                )}
              </div>
            </div>

            {/* Blockers */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <div className="flex items-center gap-2 mb-2">
                <ErrorIcon color="error" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700} color="error.main">
                  Blockers
                  {hasBlockers && (
                    <span className="ml-2 text-xs font-normal bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                      {blockers.length}
                    </span>
                  )}
                </Typography>
              </div>
              {!hasBlockers ? (
                <PassRow message="No blockers — all hard checks passed." />
              ) : (
                <div className="flex flex-col gap-2">
                  {blockers.map((msg, i) => (
                    <BlockerRow key={i} message={msg} />
                  ))}
                </div>
              )}
            </Paper>

            {/* Warnings */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <div className="flex items-center gap-2 mb-2">
                <WarningAmberIcon color="warning" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700} color="warning.main">
                  Warnings
                  {hasWarnings && (
                    <span className="ml-2 text-xs font-normal bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                      {warnings.length}
                    </span>
                  )}
                </Typography>
              </div>
              {!hasWarnings ? (
                <div className="flex items-start gap-2 border border-slate-200 bg-slate-50 rounded px-3 py-2">
                  <InfoOutlinedIcon className="text-slate-400 mt-0.5 shrink-0" fontSize="small" />
                  <span className="text-sm text-slate-600">No warnings.</span>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {warnings.map((msg, i) => (
                    <WarningRow key={i} message={msg} />
                  ))}
                </div>
              )}
            </Paper>

            {/* Override + notes */}
            <Paper variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {allClear ? "Ready to close" : hasBlockers ? "Resolve blockers before closing" : "Override warnings"}
              </Typography>

              {hasWarnings && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={forceWarnings}
                      onChange={(e) => setForceWarnings(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <span className="text-sm">
                      Acknowledge warnings and proceed (recorded in audit log)
                    </span>
                  }
                />
              )}

              <TextField
                label="Notes (optional)"
                size="small"
                multiline
                minRows={2}
                maxRows={5}
                fullWidth
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Why this period is being closed, who reviewed, exceptions, etc."
                helperText="Stored in the period_close_runs audit row."
                disabled={closeSuccess}
              />

              {hasBlockers && (
                <Alert severity="error" icon={<ErrorIcon />}>
                  Resolve all blockers above before this period can be closed.
                  Posting or deleting draft journals is the typical fix.
                </Alert>
              )}

              <div className="flex gap-2">
                <Link to="/periods">
                  <Button variant="outlined" sx={{ textTransform: "none" }}>
                    Back to Periods
                  </Button>
                </Link>
                <Button
                  variant="contained"
                  color={hasWarnings && forceWarnings ? "warning" : "primary"}
                  disabled={!canClose}
                  onClick={() => closeMut.mutate()}
                  sx={{ textTransform: "none" }}
                >
                  {closeMut.isPending
                    ? "Closing…"
                    : hasWarnings && forceWarnings
                    ? "Close with overrides"
                    : "Close Period"}
                </Button>
              </div>
            </Paper>
          </div>
        )}
      </div>
    </div>
  );
}
